import React, { useState, useEffect, useRef } from "react";
import { Card, CardHeader, CardTitle } from "../ui/card";

interface GalleryWidgetProps {
  id: string;
  title: string;
  images?: string[];
  instanceId?: string;
  show_image_time?: number;
}

const GalleryWidget: React.FC<GalleryWidgetProps> = ({ title, images = [], show_image_time = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<Record<number, boolean>>({});
  const [errors, setErrors] = useState<Record<number, boolean>>({});
  const [progress, setProgress] = useState(100);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastTimeRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto advance to next image
  useEffect(() => {
    if (images.length <= 1) return;
    
    // Cancel any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Only start timer if the current image is loaded
    if (imagesLoaded[currentIndex]) {
      // Reset progress
      setProgress(100);
      lastTimeRef.current = Date.now();
      
      // Setup progress bar update interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - lastTimeRef.current;
        const remaining = Math.max(0, 100 - (elapsed / show_image_time) * 100);
        setProgress(remaining);
      }, 16); // ~60fps update
      
      // Setup timeout for next image
      timerRef.current = setTimeout(() => {
        goToNextImage();
      }, show_image_time);
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentIndex, imagesLoaded, images.length, show_image_time]);

  const handleImageLoad = (index: number) => {
    setImagesLoaded(prev => ({
      ...prev,
      [index]: true
    }));
  };

  const handleImageError = (index: number) => {
    setErrors(prev => ({
      ...prev,
      [index]: true
    }));
  };

  const goToNextImage = () => {
    setCurrentIndex(prev => (prev + 1) % images.length);
  };

  const goToPrevImage = () => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  };

  // Pause auto-scroll when hovering
  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Resume auto-scroll when mouse leaves
  const handleMouseLeave = () => {
    if (images.length <= 1 || !imagesLoaded[currentIndex]) return;
    
    // Calculate remaining time based on current progress
    const remainingTime = (progress / 100) * show_image_time;
    
    lastTimeRef.current = Date.now() - (show_image_time - remainingTime);
    
    // Setup progress bar update interval
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastTimeRef.current;
      const remaining = Math.max(0, 100 - (elapsed / show_image_time) * 100);
      setProgress(remaining);
    }, 16);
    
    // Setup timeout for next image
    timerRef.current = setTimeout(() => {
      goToNextImage();
    }, remainingTime);
  };

  // Show nothing if no images
  if (!images.length) {
    return (
      <Card className="overflow-hidden h-full">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <div className="p-6 text-center text-muted-foreground">
          Geen afbeeldingen beschikbaar
        </div>
      </Card>
    );
  }

  return (
    <Card 
      className="overflow-hidden h-full" 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative w-full">
        {/* Navigation dots */}
        <div className="absolute bottom-8 left-0 right-0 z-20 flex justify-center gap-1">
          {images.map((_, idx) => (
            <button
              key={idx}
              className={`w-2 h-2 rounded-full ${
                idx === currentIndex ? "bg-white" : "bg-white/50"
              }`}
              onClick={() => setCurrentIndex(idx)}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-4 left-4 right-4 h-1 bg-white/10 rounded overflow-hidden z-20">
          <div 
            className="h-full bg-white/30 transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Navigation arrows */}
        <button
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/30 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/50"
          onClick={goToPrevImage}
          aria-label="Previous image"
        >
          &#10094;
        </button>
        <button
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/30 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/50"
          onClick={goToNextImage}
          aria-label="Next image"
        >
          &#10095;
        </button>

        {/* Loading state */}
        {!imagesLoaded[currentIndex] && !errors[currentIndex] && (
          <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center">
            <span className="text-muted-foreground text-sm">Afbeelding laden...</span>
          </div>
        )}
        
        {/* Error state */}
        {errors[currentIndex] && (
          <div className="absolute inset-0 bg-muted flex items-center justify-center">
            <span className="text-muted-foreground text-sm">Kan afbeelding niet laden</span>
          </div>
        )}
        
        {/* Images */}
        <div className="w-full">
          {images.map((src, idx) => (
            <img 
              key={idx}
              src={src} 
              alt={`${title} - image ${idx + 1}`}
              className={`w-full transition-opacity duration-300 ${
                idx === currentIndex && imagesLoaded[idx] ? 'block opacity-100' : 'hidden opacity-0'
              }`}
              onLoad={() => handleImageLoad(idx)}
              onError={() => handleImageError(idx)}
              style={{ display: idx === currentIndex ? 'block' : 'none' }}
            />
          ))}
        </div>
        
        {/* Gradient overlay for title */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 via-black/30 to-transparent h-20 z-10"></div>
        
        {/* Title on top of image */}
        <CardHeader className="absolute top-0 left-0 right-0 z-20 pb-2">
          <CardTitle className="text-white drop-shadow-md">{title}</CardTitle>
        </CardHeader>
      </div>
    </Card>
  );
};

export default GalleryWidget; 